import type { CSSProperties } from 'react';

/**
 * 현장 조회 탭들이 함께 쓰는 값·변환 — 컴포넌트가 아닌 것만 모은다.
 *
 * 컴포넌트 파일({@code ui.tsx})과 나눈 이유는 Fast Refresh 다. 한 파일이 컴포넌트와 상수를 함께
 * 내보내면 편집할 때마다 모듈 전체가 다시 평가돼 화면 상태가 날아간다.
 */

export const smallButton: CSSProperties = { fontSize: '12px', padding: '4px 10px' };

/** 카드가 한 줄에 여러 개 서는 화면(전 현장 대화방 목록)용. 버튼 줄이 두 줄로 접히지 않게 한다. */
export const tinyButton: CSSProperties = { fontSize: '11px', padding: '3px 8px' };

export const inputStyle: CSSProperties = { fontSize: '13px', padding: '8px 10px', width: '100%' };

/** 오늘 날짜(YYYY-MM-DD). 입력 폼의 기본값으로 쓴다. */
export function today(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 금액 표시. 천 단위 구분만 하고 단위는 붙이지 않는다(원 단위로 저장한다). */
export function formatAmount(value: number | null): string {
  return value == null ? '-' : value.toLocaleString('ko-KR');
}

/**
 * 숫자 입력을 숫자로. 빈칸은 null 로 보내 "0 원" 과 "안 적음" 을 구분한다.
 *
 * <h2>천 단위 쉼표를 걷어낸다 (사용자 지적 2026-09-15 「1000 넘어가는데 처리가 안돼네」)</h2>
 * 사람은 수량·금액을 <b>1,000</b> 으로 친다 — 표에 그렇게 찍혀 있으니 그렇게 돌려준다.
 * 그런데 {@code Number("1,000")} 은 {@code NaN} 이라 그대로 두면 <b>천 단위를 넘는 값만</b>
 * 조용히 사라진다(세 자리까지는 쉼표를 찍을 일이 없어 멀쩡하게 들어간다).
 *
 * <p>공백·전각 쉼표도 함께 지운다. 엑셀에서 긁어 온 칸에 그것들이 섞여 온다.
 */
export function toNumberOrNull(value: string): number | null {
  // 전각 쉼표·전각 공백은 escape 로 적는다 — 글자 그대로 두면 눈에 안 보여 다음 사람이 지운다.
  const trimmed = value.replace(/[,，\s\u3000]/g, '').trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * 숫자 <b>입력칸에 보이는</b> 글자 — 천 단위 쉼표를 넣는다 (사용자 지적 2026-09-15
 * 「금액 [천원] 계획수량 실적수량 여기 항목들 숫자에 , 안들어가」).
 *
 * <p>읽기 칸은 이미 쉼표로 찍혀 있었는데({@code toLocaleString}) 입력칸만 맨 숫자였다 —
 * 같은 값이 같은 화면에서 <b>1,250,000</b> 과 <b>1250000</b> 두 모양으로 보였고, 뒤엣것은
 * 자릿수를 손가락으로 세어야 한다. 금액은 자릿수가 곧 뜻이다.
 *
 * <p><b>칠 때가 아니라 손을 뗄 때</b> 넣는다. 한 글자마다 다시 찍으면 쉼표가 끼어드는 만큼
 * 커서가 뒤로 밀려 「1,0,00」 같은 글자가 남는다. 저장할 때는 {@link toNumberOrNull} 이 걷어낸다.
 *
 * <p>숫자로 못 읽으면 <b>적은 그대로</b> 돌려준다 — 치다 만 글자(「1.」·「-」)를 빼앗지 않는다.
 * 소수는 세 자리까지다(DB 의 {@code numeric(15,3)} 과 같은 자다).
 */
export function withThousands(value: string): string {
  const parsed = toNumberOrNull(value);
  if (parsed == null) return value;
  return parsed.toLocaleString('ko-KR', { maximumFractionDigits: 3 });
}

/** 서버가 준 오류 메시지. `ApiRequestError` 는 message 에 한국어 사유가 들어 있다. */
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '저장하지 못했습니다.';
}

/** 보미기술집과 같은 공종 값(DOC-12 §0 확정). 서버 CHECK 와 한 벌이다. */
export const TRADES = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'] as const;
